import { Button, DatePicker, Input, Modal, Notice, SelectButton, Textarea } from '@bennows/boows-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFailure, fieldErrorCodes } from '../../lib/api';
import { endOfViennaDay, viennaDay } from '../../lib/format';
import { useToast } from '../../lib/toasts';
import { BILLING_QUERY, useBillingActions, type BillingMode, type TenantBilling } from './useTenantBilling';

const FORM_ID = 'billing-change';
const REASON_MIN = 3;
const REASON_MAX = 500;
const MODES: readonly BillingMode[] = ['metered', 'exempt'];

const FAILURES: Record<string, string> = {
  'not-in-trial': 'tenantBilling.change.notInTrial',
  'tenant-not-billed': 'tenantBilling.unlinkedText',
  'platform-unavailable': 'tenantBilling.adjust.platformDown',
  network: 'common.errorNetwork',
};
const TRIAL_FAILURES: Record<string, string> = { not_later: 'tenantBilling.change.trialNotLater', too_long: 'tenantBilling.change.trialTooLong' };

/**
 * Billing mode and a later trial end on the license platform (REQ-140-022).
 * Switching to exempt also asks for the company name typed out (REQ-140-024).
 * The trial end is known only when the billing tab was loaded (`billing:read`);
 * otherwise the date is optional and the platform refuses it outside a trial.
 */
export function BillingChangeDialog({
  tenantId,
  tenantName,
  mode,
  onClose,
}: {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly mode: BillingMode;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const known = useQueryClient().getQueryData<TenantBilling>([BILLING_QUERY, tenantId])?.subscription;
  // undefined: not known here; null: not in a trial, nothing to extend.
  const trialEndsAt = known === undefined ? undefined : known?.status === 'trial' ? known.trial_ends_at : null;
  const toast = useToast();
  const { change } = useBillingActions(tenantId);
  const [nextMode, setNextMode] = useState<BillingMode>(mode);
  const [trialDay, setTrialDay] = useState(trialEndsAt ? viennaDay(trialEndsAt) : '');
  const [reason, setReason] = useState('');
  const [typedName, setTypedName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const toExempt = nextMode === 'exempt' && mode !== 'exempt';
  const trialChanged = trialEndsAt !== null && trialDay !== '' && (trialEndsAt === undefined || trialDay !== viennaDay(trialEndsAt));
  const reasonMissing = reason.trim().length < REASON_MIN;
  const nameMismatch = toExempt && typedName.trim() !== tenantName;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (reasonMissing || nameMismatch) return;
    if (nextMode === mode && !trialChanged) return onClose();
    change.mutate(
      { ...(nextMode !== mode ? { billingMode: nextMode } : {}), ...(trialChanged ? { trialEndsAt: endOfViennaDay(trialDay) } : {}), reason: reason.trim() },
      { onSuccess: () => (toast(t('tenantBilling.change.done', { name: tenantName })), onClose()) },
    );
  };

  const trialCode = change.error instanceof ApiFailure ? fieldErrorCodes(change.error.body, 'trial_ends_at')[0] : undefined;
  const trialError = trialCode ? (TRIAL_FAILURES[trialCode] ?? 'common.errorUnexpected') : null;
  const failure = trialError ? null : change.error instanceof ApiFailure ? (FAILURES[change.error.slug] ?? 'common.errorUnexpected') : change.error ? 'common.errorUnexpected' : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenantBilling.change.title')}
      description={tenantName}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} variant={toExempt ? 'danger' : 'primary'} loading={change.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="ui-console-form" onSubmit={onSubmit} noValidate>
        <SelectButton
          label={t('tenantBilling.change.mode')}
          value={nextMode}
          options={MODES.map((id) => ({ id, label: t(`billingStates.${id}`) }))}
          onChange={(id) => setNextMode(id as BillingMode)}
        />
        {nextMode !== mode ? (
          <Notice tone={toExempt ? 'warning' : 'info'} role="none">
            {t(`tenantBilling.change.effects.${nextMode}`)}
          </Notice>
        ) : null}
        {trialEndsAt !== null ? (
          <DatePicker
            label={t(trialEndsAt ? 'tenantBilling.change.trialEnd' : 'tenantBilling.change.trialEndOptional')}
            value={trialDay}
            onChange={setTrialDay}
            {...(trialEndsAt ? { min: viennaDay(trialEndsAt) } : {})}
            invalid={trialError !== null}
            help={trialError ? t(trialError) : t('tenantBilling.change.trialHelp')}
          />
        ) : null}
        <Textarea
          label={t('tenantActions.reason')}
          value={reason}
          onChange={setReason}
          rows={3}
          maxLength={REASON_MAX}
          required
          invalid={submitted && reasonMissing}
          help={submitted && reasonMissing ? t('tenantActions.reasonMissing') : t('tenantActions.reasonHelp')}
        />
        {toExempt ? (
          <Input
            label={t('tenantActions.suspend.confirmName', { name: tenantName })}
            value={typedName}
            onChange={setTypedName}
            required
            invalid={submitted && nameMismatch}
            help={submitted && nameMismatch ? t('tenantActions.suspend.nameMismatch') : undefined}
          />
        ) : null}
        {failure ? (
          <p className="ui-auth__message" role="alert">
            {t(failure)}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
